import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useAppSelector } from "../hooks/storeHook";
import {
  downloadThreadCsvExport,
  parseCsvExportSearchParams,
} from "../shared/utils/csvExportDownload";

type Status = "loading" | "success" | "error";

/** Authenticated CSV download page for thread-scoped exports from export_data_to_csv. */
const CsvExportDownload: React.FC = () => {
  const [searchParams] = useSearchParams();
  const aiThreadId = useAppSelector((state: any) => state.chat?.aiThreadId);
  const selectedUserThreadId = useAppSelector((state: any) => state.chat?.selectedUserThreadId);
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState("Preparing download…");

  const threadId = useMemo(() => {
    return String(aiThreadId ?? selectedUserThreadId ?? "").trim() || null;
  }, [aiThreadId, selectedUserThreadId]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        const parsed = parseCsvExportSearchParams(searchParams);
        if (!parsed) throw new Error("Invalid CSV download link.");
        if (!threadId) throw new Error("Download unavailable: missing thread context.");

        await downloadThreadCsvExport(threadId, parsed.exportId, parsed.filename);
        if (!mounted) return;
        setStatus("success");
        setMessage("CSV download started. You can return to the chat thread.");
      } catch (error: any) {
        if (!mounted) return;
        setStatus("error");
        setMessage(error?.message || "Failed to download CSV.");
      }
    };

    void run();
    return () => {
      mounted = false;
    };
  }, [searchParams, threadId]);

  return (
    <Box sx={{ width: "100%", minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
      <Paper elevation={1} sx={{ width: "100%", maxWidth: 680, p: 3, borderRadius: 2 }}>
        {status === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontFamily: "var(--theo-font-sans)" }}>Preparing download…</Typography>
          </Box>
        )}
        {status === "success" && (
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#1b5e20" }}>{message}</Typography>
        )}
        {status === "error" && (
          <Box>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#b3261e", fontWeight: 600, mb: 0.5 }}>
              Download failed
            </Typography>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#444" }}>{message}</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CsvExportDownload;
